// Wave 3.51 — Edit Transaction Approval Workflow UI.
//
// Layout mirrors the Delete Requests page but adapts to the edit-only
// workflow: only Company Admin / Super Admin can initiate, only Super
// Admin can act on pending_sa requests, and the request body carries a
// `patch` (proposed changes) + `entity_snapshot` (before values) which
// we render as a before/after diff.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { EDIT_ENTITY_TYPE_LABELS } from '@/lib/editFlow';
import { Check, X, RefreshCw, Search, History, Inbox, ChevronDown, ChevronRight, PencilLine, ExternalLink } from 'lucide-react';

const STATUS_INFO = {
  pending_sa: { label: 'Awaiting Super Admin', tone: 'bg-blue-50 text-blue-700' },
  approved_executed: { label: 'Approved & Applied', tone: 'bg-emerald-50 text-emerald-700' },
  rejected_sa: { label: 'Rejected by SA', tone: 'bg-rose-50 text-rose-700' },
  sent_back_to_user: { label: 'Sent back to requester', tone: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-slate-100 text-slate-600' },
};

export default function EditRequests() {
  return (
    <AppShell
      title="Edit Requests"
      subtitle="Edits to Goldsmith Issues/Returns, Vendor Purchase Returns, Packet Transfer/Re-cut/Send-to-Broken, and Jewellery Receives — within the T+1 IST window. Workflow: CA → SA (Super Admin applies)."
    >
      <EditRequestsContent />
    </AppShell>
  );
}

function EditRequestsContent() {
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const [tab, setTab] = useState('awaiting');
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ status: 'all', search: '' });
  const [counts, setCounts] = useState({ awaiting_me: 0, pending_sa: 0, sent_back_to_user_for_me: 0 });

  const canSeePage = isSuperAdmin || isCompanyAdmin;

  const load = async () => {
    if (!canSeePage) { setList([]); return; }
    try {
      const params = {};
      if (tab === 'awaiting') params.role_scope = 'awaiting_me';
      else if (tab === 'mine') params.role_scope = 'my_requests';
      if (filter.status !== 'all' && tab === 'history') params.status = filter.status;
      const { data } = await api.get('/edit-requests/', { params });
      let docs = data || [];
      if (tab === 'history') {
        docs = docs.filter((r) => !['pending_sa', 'sent_back_to_user'].includes(r.status));
      }
      if (filter.search) {
        const q = filter.search.toLowerCase();
        docs = docs.filter(
          (d) =>
            (d.entity_label || '').toLowerCase().includes(q) ||
            (d.entity_type || '').includes(q) ||
            (d.requested_by_email || '').toLowerCase().includes(q)
        );
      }
      setList(docs);
    } catch (e) {
      toast.error(extractError(e, 'Load failed'));
    }
  };

  const loadCounts = async () => {
    if (!canSeePage) return;
    try {
      const { data } = await api.get('/edit-requests/counts');
      setCounts(data);
    } catch {}
  };

  useEffect(() => { load(); loadCounts(); /* eslint-disable-next-line */ }, [tab, filter.status]); // eslint-disable-line

  if (!canSeePage) {
    return (
      <Card className="p-10 text-center text-sm text-slate-500" data-testid="edit-requests-forbidden">
        Only Company Admin and Super Admin can see this page.
      </Card>
    );
  }

  return (
    <div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--divider)]">
          <TabsTrigger value="awaiting" data-testid="edit-requests-tab-awaiting">
            <Inbox size={14} className="mr-1.5" />
            Awaiting You
            {counts.awaiting_me > 0 && (
              <Badge className="ml-2 bg-rose-50 text-rose-700 text-[10px]" data-testid="edit-requests-awaiting-count">
                {counts.awaiting_me}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="mine" data-testid="edit-requests-tab-mine">
            My Requests
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="edit-requests-tab-history">
            <History size={14} className="mr-1.5" /> All / History
          </TabsTrigger>
        </TabsList>

        <div className="sticky-filter-bar mt-4 flex flex-wrap gap-2 items-end justify-between rounded-lg px-3" data-testid="edit-requests-filter-bar">
          <div className="flex gap-2 items-end">
            <div className="relative">
              <Label className="text-[11px] text-slate-500">Search</Label>
              <Search size={12} className="absolute left-3 bottom-2.5 text-slate-400" />
              <Input
                className="pl-7 h-9 w-56"
                placeholder="entity, requester, or type"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                data-testid="edit-requests-search-input"
              />
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { load(); loadCounts(); }}
            data-testid="edit-requests-refresh-button"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>

        <TabsContent value="awaiting" className="mt-4">
          <EditRequestList list={list} reload={() => { load(); loadCounts(); }} canAct />
        </TabsContent>
        <TabsContent value="mine" className="mt-4">
          <EditRequestList list={list} reload={() => { load(); loadCounts(); }} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <EditRequestList list={list} reload={() => { load(); loadCounts(); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EditRequestList({ list, reload, canAct }) {
  const [expanded, setExpanded] = useState({});
  const { user } = useAuth();
  if (list.length === 0) {
    return (
      <Card className="p-10 text-center text-sm text-slate-500" data-testid="edit-requests-empty">
        No edit requests to show here.
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="edit-requests-list">
      {list.map((r) => (
        <EditRequestCard
          key={r.id}
          r={r}
          expanded={!!expanded[r.id]}
          toggle={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}
          reload={reload}
          canAct={canAct}
          currentUserId={user?.id}
        />
      ))}
    </div>
  );
}

function EditRequestCard({ r, expanded, toggle, reload, canAct, currentUserId }) {
  const { isSuperAdmin } = useAuth();
  const info = STATUS_INFO[r.status] || { label: r.status, tone: 'bg-slate-100' };
  const isOwn = currentUserId === r.requested_by;
  const canSAAct = r.status === 'pending_sa' && isSuperAdmin;
  const canUserAct = r.status === 'sent_back_to_user' && isOwn;

  const action = async (act, note = '') => {
    try {
      await api.post(`/edit-requests/${r.id}/action`, { action: act, note });
      toast.success(`Marked as ${act.replace('_', ' ')}`);
      reload();
    } catch (e) {
      toast.error(extractError(e, 'Action failed'));
    }
  };

  // Link target on the entity
  const entityHref =
    r.entity_type === 'goldsmith_issue'
      ? `/issue-memos/${r.entity_id}`
      : r.entity_type === 'goldsmith_return'
        ? `/return-memos/${r.entity_id}`
        : null;

  return (
    <Card className="p-4" data-testid={`edit-request-card-${r.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <button
          onClick={toggle}
          className="text-slate-500 hover:text-slate-900 mt-0.5"
          data-testid={`edit-request-toggle-${r.id}`}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="text-[10px] capitalize">
              {EDIT_ENTITY_TYPE_LABELS[r.entity_type] || r.entity_type.replace('_', ' ')}
            </Badge>
            <span
              className="font-medium text-[var(--ink-navy)] truncate"
              data-testid={`edit-request-label-${r.id}`}
            >
              {r.entity_label}
            </span>
            {entityHref && (
              <Link
                to={entityHref}
                className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
                data-testid={`edit-request-open-entity-${r.id}`}
              >
                Open <ExternalLink size={10} />
              </Link>
            )}
            <Badge className={`text-[10px] ${info.tone}`} data-testid={`edit-request-status-${r.id}`}>
              {info.label}
            </Badge>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Requested by{' '}
            <span className="font-medium text-slate-700">{r.requested_by_email}</span> on{' '}
            {formatDateTime(r.requested_at)}
          </div>
          {r.reason && (
            <div className="text-xs text-slate-600 mt-1">Reason: {r.reason}</div>
          )}
        </div>
        {canAct && (
          <div className="flex flex-wrap gap-2">
            {canSAAct && (
              <>
                <ActionButton
                  variant="approve"
                  onConfirm={(note) => action('approve', note)}
                  testid={`edit-request-approve-${r.id}`}
                />
                <ActionButton
                  variant="send_back"
                  onConfirm={(note) => action('send_back', note)}
                  testid={`edit-request-sendback-${r.id}`}
                />
                <ActionButton
                  variant="reject"
                  onConfirm={(note) => action('reject', note)}
                  testid={`edit-request-reject-${r.id}`}
                />
              </>
            )}
            {canUserAct && (
              <>
                <ActionButton
                  variant="resubmit"
                  onConfirm={(note) => action('resubmit', note)}
                  testid={`edit-request-resubmit-${r.id}`}
                />
                <ActionButton
                  variant="cancel"
                  onConfirm={(note) => action('cancel', note)}
                  testid={`edit-request-cancel-${r.id}`}
                />
              </>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[var(--divider)] space-y-3">
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1">Proposed changes</div>
            <PatchDiff r={r} />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 mb-2">History</div>
            <div className="space-y-1.5 text-xs text-slate-600" data-testid={`edit-request-history-${r.id}`}>
              {(r.history || []).map((h, i) => (
                <div key={i} className="flex justify-between gap-2">
                  <span>
                    <span className="capitalize font-medium">{(h.action || '').replace('_', ' ')}</span>
                    {h.role && <span className="text-slate-400"> · {String(h.role).replace('_', ' ')}</span>}
                    {' '}by <span className="font-mono">{h.by_email}</span>
                    {h.note && <span className="text-slate-500"> — {h.note}</span>}
                  </span>
                  <span className="text-slate-400 whitespace-nowrap">{formatDateTime(h.at)}</span>
                </div>
              ))}
            </div>
          </div>
          {r.executor_summary && (
            <details>
              <summary className="text-[11px] text-slate-500 cursor-pointer">
                Executor summary
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto text-[10px] bg-slate-50 p-2 rounded border border-[var(--divider)]">
                {JSON.stringify(r.executor_summary, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

function PatchDiff({ r }) {
  const snap = r.entity_snapshot || {};
  const patch = r.patch || {};
  const rows = [];
  for (const k of ['reference', 'note']) {
    if (k in patch && patch[k] !== snap[k]) {
      rows.push({ scope: 'Top level', field: k, before: snap[k] ?? '—', after: patch[k] ?? '—' });
    }
  }
  // Per-line diff
  const linesById = {};
  for (const ln of (snap.lines || [])) {
    if (ln && ln.line_id) linesById[ln.line_id] = ln;
    if (ln && ln.ledger_entry_id) linesById[ln.ledger_entry_id] = ln;
  }
  for (const lp of (patch.lines || [])) {
    const key = lp.line_id || lp.ledger_entry_id;
    const ln = linesById[key] || {};
    const label =
      ln.packet_no || ln.purity_name || lp.line_id || lp.ledger_entry_id || 'Line';
    for (const k of Object.keys(lp)) {
      if (k === 'line_id' || k === 'ledger_entry_id') continue;
      if (lp[k] !== ln[k]) {
        rows.push({
          scope: label,
          field: k,
          before: ln[k] ?? '—',
          after: lp[k] ?? '—',
        });
      }
    }
  }
  if (rows.length === 0) {
    return (
      <div className="text-xs text-slate-500">No diffable fields in patch.</div>
    );
  }
  return (
    <div
      className="overflow-x-auto border border-[var(--divider)] rounded"
      data-testid={`edit-request-patch-diff-${r.id}`}
    >
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-2 py-1.5">Where</th>
            <th className="text-left px-2 py-1.5">Field</th>
            <th className="text-left px-2 py-1.5">Before</th>
            <th className="text-left px-2 py-1.5">After</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-[var(--divider)]">
              <td className="px-2 py-1.5 text-slate-500">{row.scope}</td>
              <td className="px-2 py-1.5 font-mono">{row.field}</td>
              <td className="px-2 py-1.5 text-slate-700 font-mono">
                {String(row.before)}
              </td>
              <td className="px-2 py-1.5 text-amber-700 font-mono">
                {String(row.after)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionButton({ variant, onConfirm, testid }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const meta = {
    approve: {
      label: 'Approve',
      icon: <Check size={14} className="mr-1" />,
      cls: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    },
    reject: {
      label: 'Reject',
      icon: <X size={14} className="mr-1" />,
      cls: 'bg-rose-600 hover:bg-rose-700 text-white',
    },
    send_back: {
      label: 'Send back',
      icon: <RefreshCw size={14} className="mr-1" />,
      cls: '',
    },
    resubmit: {
      label: 'Resubmit',
      icon: <RefreshCw size={14} className="mr-1" />,
      cls: 'luxury-button',
    },
    cancel: {
      label: 'Cancel request',
      icon: <X size={14} className="mr-1" />,
      cls: 'bg-slate-200 hover:bg-slate-300',
    },
  };
  const m = meta[variant] || meta.approve;
  const requireNote = variant === 'reject' || variant === 'send_back';
  return (
    <>
      <Button
        size="sm"
        variant={variant === 'send_back' ? 'outline' : 'default'}
        className={m.cls}
        onClick={() => setOpen(true)}
        data-testid={testid}
      >
        {m.icon} {m.label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{m.label}?</DialogTitle>
            <DialogDescription>
              {requireNote
                ? 'A note is required so the requester understands the decision.'
                : 'Add an optional note to explain your action.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>
              Note {requireNote && <span className="text-rose-600">*</span>}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context (visible to the requester)"
              data-testid={`${testid}-note`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onConfirm(note);
                setOpen(false);
                setNote('');
              }}
              disabled={requireNote && !note.trim()}
              data-testid={`${testid}-confirm`}
              className={m.cls}
            >
              Confirm {m.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
